import { WholesalesListResponse } from '@domains/login';

import { RequestPayload } from '@api/client/types';
import {
    AddressesCreatePayload,
    AddressesCreateResult,
    AddressesDeletePayload,
    AddressesDeleteResult,
    AddressesListPayload,
    AddressesListResult,
    AddressesSuggestionsPayload,
    AddressesSuggestionsResult,
    AddressesUpdatePayload,
    AddressesUpdateResult,
    CartAddProductsPayload,
    CartAddProductsResult,
    CartClearPayload,
    CartClearResult,
    CartSetQuantityProductPayload,
    CartSetQuantityProductResult,
    CitiesSuggestionsPayload,
    CitiesSuggestionsResult,
    FavoriteProductsAddPayload,
    FavoriteProductsAddResult,
    FavoriteProductsListPayload,
    FavoriteProductsListResult,
    FavoriteProductsRemovePayload,
    FavoriteProductsRemoveResult,
    FavoriteRecipesAddPayload,
    FavoriteRecipesAddResult,
    FavoriteRecipesRemovePayload,
    FavoriteRecipesRemoveResult,
    FeedbackSubmitPayload,
    FeedbackSubmitResult,
    GetCartPayload,
    GetCartResult,
    IndividualCalculatePayload,
    IndividualCalculateResult,
    IndividualCreatePayload,
    IndividualCreateResult,
    IndividualLoginPayload,
    IndividualLoginResult,
    LogoutPayload,
    LogoutResult,
    OrderCancelPayload,
    OrderCancelResult,
    OrderCheckUnavailableItemsPayload,
    OrderCheckUnavailableItemsResult,
    OrderIndividualDetailsPayload,
    OrderIndividualDetailsResult,
    OrderPayPayload,
    OrderPayResult,
    OrderRepeatPayload,
    OrderRepeatResult,
    OrderWholesaleDetailsPayload,
    OrderWholesaleDetailsResult,
    PartnershipRequestPayload,
    PartnershipRequestResult,
    ProductsAutocompletePayload,
    ProductsAutocompleteResult,
    ProfilePayload,
    ProfileResult,
    ProfileUpdatePayload,
    ProfileUpdateResult,
    ReceivingDatesDeliveryListPayload,
    ReceivingDatesDeliveryListResult,
    ReceivingDatesPickupListPayload,
    ReceivingDatesPickupListResult,
    RequestCodePayload,
    RequestCodeResult,
    SurveyCompletePayload,
    SurveyCompleteResult,
    SurveyDismissPayload,
    SurveyDismissResult,
    WholesaleAddressesPayload,
    WholesaleAddressesResult,
    WholesaleCalculatePayload,
    WholesaleCalculateResult,
    WholesaleCreatePayload,
    WholesaleCreateResult,
    WholesaleLoginPayload,
    WholesaleLoginResult,
    WholesalesListPayload,
} from '@api/protocol';

import { trackException } from '@utils/sentry';

import { ApiClientClass } from './client';
import { CookieAuth } from './client/auth';

const ApiClient = new ApiClientClass(CookieAuth());
ApiClient.setSentryHandler(trackException);

const makeApiRequest =
    <Request extends RequestPayload, Response = void>(method: 'get' | 'post', url: string) =>
    (payload: Request | Record<string | number | symbol, never>): Promise<Response> => {
        if (method === 'post') return ApiClient.post<Response, Request>(url, payload);
        return ApiClient.get<Response, Request>(url, payload);
    };

export const apiFeedbackSubmit = makeApiRequest<FeedbackSubmitPayload, FeedbackSubmitResult>(
    'post',
    '/api/feedback/submit',
);

export const apiRequestCode = makeApiRequest<RequestCodePayload, RequestCodeResult>(
    'post',
    '/api/auth/individual/request_code',
);

export const apiIndividualLogin = makeApiRequest<IndividualLoginPayload, IndividualLoginResult>(
    'post',
    '/api/auth/individual/cookie/login',
);

export const apiIndividualLogout = makeApiRequest<LogoutPayload, LogoutResult>(
    'post',
    '/api/auth/individual/cookie/logout',
);

export const apiWholesaleLogout = makeApiRequest<LogoutPayload, LogoutResult>(
    'post',
    '/api/auth/wholesale/cookie/logout',
);

export const apiWholesaleLogin = makeApiRequest<WholesaleLoginPayload, WholesaleLoginResult>(
    'post',
    '/api/auth/wholesale/cookie/login',
);

export const apiWholesalesList = makeApiRequest<WholesalesListPayload, WholesalesListResponse>(
    'get',
    '/api/auth/wholesale/list',
);

export const apiProfile = makeApiRequest<ProfilePayload, ProfileResult>(
    'get',
    '/api/user/profile/details',
);

export const apiWholesaleAddresses = makeApiRequest<
    WholesaleAddressesPayload,
    WholesaleAddressesResult
>('get', '/api/user/profile/establishment/list');

export const apiProfileUpdate = makeApiRequest<ProfileUpdatePayload, ProfileUpdateResult>(
    'post',
    '/api/user/profile/update',
);

export const apiPartnershipRequest = makeApiRequest<
    PartnershipRequestPayload,
    PartnershipRequestResult
>('post', '/api/partnership/request');

export const apiFavoriteRecipesAdd = makeApiRequest<
    FavoriteRecipesAddPayload,
    FavoriteRecipesAddResult
>('post', '/api/user/profile/favorite/recipes/add');

export const apiFavoriteRecipesRemove = makeApiRequest<
    FavoriteRecipesRemovePayload,
    FavoriteRecipesRemoveResult
>('post', '/api/user/profile/favorite/recipes/remove');

export const apiAddressesList = makeApiRequest<AddressesListPayload, AddressesListResult>(
    'get',
    '/api/user/profile/address/list',
);

export const apiAddressesCreate = makeApiRequest<AddressesCreatePayload, AddressesCreateResult>(
    'post',
    '/api/user/profile/address/create',
);

export const apiAddressesUpdate = makeApiRequest<AddressesUpdatePayload, AddressesUpdateResult>(
    'post',
    '/api/user/profile/address/update',
);

export const apiAddressesDelete = makeApiRequest<AddressesDeletePayload, AddressesDeleteResult>(
    'post',
    '/api/user/profile/address/delete',
);

export const apiAddressesSuggestions = makeApiRequest<
    AddressesSuggestionsPayload,
    AddressesSuggestionsResult
>('get', '/api/suggestions/city_addresses');

export const apiCitiesSuggestions = makeApiRequest<
    CitiesSuggestionsPayload,
    CitiesSuggestionsResult
>('get', '/api/suggestions/cities');

export const apiProductsAutocomplete = makeApiRequest<
    ProductsAutocompletePayload,
    ProductsAutocompleteResult
>('get', '/api/products_catalog/products/autocomplete');

export const apiCart = makeApiRequest<GetCartPayload, GetCartResult>('get', '/api/cart/details');

export const apiCartAddProducts = makeApiRequest<CartAddProductsPayload, CartAddProductsResult>(
    'post',
    '/api/cart/add_products',
);

export const apiCartSetQuantityProduct = makeApiRequest<
    CartSetQuantityProductPayload,
    CartSetQuantityProductResult
>('post', '/api/cart/set_quantity');

export const apiCartClear = makeApiRequest<CartClearPayload, CartClearResult>(
    'post',
    '/api/cart/clear',
);

export const apiFavoriteProductsList = makeApiRequest<
    FavoriteProductsListPayload,
    FavoriteProductsListResult
>('get', '/api/favorite_products/list');

export const apiFavoriteProductsAdd = makeApiRequest<
    FavoriteProductsAddPayload,
    FavoriteProductsAddResult
>('post', '/api/favorite_products/add');

export const apiFavoriteProductsRemove = makeApiRequest<
    FavoriteProductsRemovePayload,
    FavoriteProductsRemoveResult
>('post', '/api/favorite_products/remove');

export const apiOrderIndividualDetails = makeApiRequest<
    OrderIndividualDetailsPayload,
    OrderIndividualDetailsResult
>('get', '/api/orders/individual/details');

export const apiOrderWholesaleDetails = makeApiRequest<
    OrderWholesaleDetailsPayload,
    OrderWholesaleDetailsResult
>('get', '/api/orders/wholesale/details');

export const apiIndividualCalculate = makeApiRequest<
    IndividualCalculatePayload,
    IndividualCalculateResult
>('get', '/api/orders/individual/calculate');

export const apiIndividualCreate = makeApiRequest<IndividualCreatePayload, IndividualCreateResult>(
    'post',
    '/api/orders/individual/create',
);

export const apiOrderPay = makeApiRequest<OrderPayPayload, OrderPayResult>(
    'post',
    '/api/orders/pay',
);

export const apiOrderCancel = makeApiRequest<OrderCancelPayload, OrderCancelResult>(
    'post',
    '/api/orders/cancel',
);

export const apiOrderRepeat = makeApiRequest<OrderRepeatPayload, OrderRepeatResult>(
    'post',
    '/api/orders/repeat',
);

export const apiOrderCheckUnavailableItems = makeApiRequest<
    OrderCheckUnavailableItemsPayload,
    OrderCheckUnavailableItemsResult
>('get', '/api/orders/check_unavailable_items');

export const apiWholesaleCalculate = makeApiRequest<
    WholesaleCalculatePayload,
    WholesaleCalculateResult
>('get', '/api/orders/wholesale/calculate');

export const apiWholesaleCreate = makeApiRequest<WholesaleCreatePayload, WholesaleCreateResult>(
    'post',
    '/api/orders/wholesale/create',
);

export const apiReceivingDatesDeliveryList = makeApiRequest<
    ReceivingDatesDeliveryListPayload,
    ReceivingDatesDeliveryListResult
>('get', '/api/receiving_dates/delivery/list');

export const apiReceivingDatesPickupList = makeApiRequest<
    ReceivingDatesPickupListPayload,
    ReceivingDatesPickupListResult
>('get', '/api/receiving_dates/pickup/list');

export const apiSurveyComplete = makeApiRequest<SurveyCompletePayload, SurveyCompleteResult>(
    'post',
    '/api/survey/complete',
);

export const apiSurveyDismiss = makeApiRequest<SurveyDismissPayload, SurveyDismissResult>(
    'post',
    '/api/survey/dismiss',
);
